import { RELATION_HAS_PART, RELATION_PART_OF } from '@backstage/catalog-model';
import { MarkdownContent } from '@backstage/core-components';
import { EntityLayout, EntitySwitch } from '@backstage/plugin-catalog';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import { useEntity } from '@backstage/plugin-catalog-react';
import { CustomAboutCard } from '../catalog/CustomAboutCard/CustomAboutCard';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';

const AI_FILE_CONTENT_ANNOTATION = 'principal.com/ai-file-content';

const isAiType =
  (type: string) =>
  (entity: Entity): boolean =>
    entity.kind?.toLowerCase() === 'ai' && entity.spec?.type === type;

const useStyles = makeStyles(theme => ({
  fileCard: {
    height: '100%',
    overflow: 'hidden',
  },
  markdownWrapper: {
    overflow: 'hidden',
    '& pre': {
      background: theme.palette.background.default,
      padding: theme.spacing(1.5),
      borderRadius: theme.shape.borderRadius,
      overflowX: 'auto',
    },
    '& code': {
      fontFamily: 'monospace',
      wordBreak: 'break-all',
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
    },
    '& table': {
      display: 'block',
      overflowX: 'auto',
      maxWidth: '100%',
    },
    '& a': {
      wordBreak: 'break-all',
    },
  },
  frontmatterTable: {
    tableLayout: 'fixed' as const,
    width: '100%',
    '& td': {
      borderBottom: 'none',
      padding: theme.spacing(0.75, 1),
      verticalAlign: 'top',
      wordBreak: 'break-word',
    },
    '& td:first-child': {
      fontWeight: theme.typography.fontWeightMedium as number,
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap' as const,
      width: 140,
      paddingRight: theme.spacing(2),
      wordBreak: 'normal',
    },
  },
}));

/**
 * Parses YAML frontmatter from markdown text.
 * Returns structured fields and the body without the `---` block.
 */
function parseFrontmatterForDisplay(content: string): {
  fields: { key: string; value: string }[];
  body: string;
} {
  const match = content.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) {
    return { fields: [], body: content };
  }
  const fields: { key: string; value: string }[] = [];
  for (const line of match[1].split('\n')) {
    // Only parse top-level keys (no leading whitespace) to avoid
    // picking up nested fields like description under inputs.
    if (/^\s/.test(line)) continue;
    const colonIdx = line.indexOf(':');
    if (colonIdx < 1) continue;
    const key = line.substring(0, colonIdx).trim();
    const value = line
      .substring(colonIdx + 1)
      .trim()
      .replace(/^['"]|['"]$/g, '');
    if (key && value) fields.push({ key, value });
  }
  return { fields, body: match[2].trimStart() };
}

/** Renders the markdown body of the AI file (SKILL.md or *.agent.md). */
function EntityAiFileCard() {
  const { entity } = useEntity();
  const classes = useStyles();
  const content = entity.metadata.annotations?.[AI_FILE_CONTENT_ANNOTATION];

  if (!content) {
    return (
      <Card className={classes.fileCard}>
        <CardHeader title="File Content" />
        <Divider />
        <CardContent>
          <Typography variant="body2" color="textSecondary">
            No file content available.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const { body } = parseFrontmatterForDisplay(content);

  return (
    <Card className={classes.fileCard}>
      <CardHeader title="File Content" />
      <Divider />
      <CardContent className={classes.markdownWrapper}>
        <MarkdownContent content={body || content} />
      </CardContent>
    </Card>
  );
}

/** Renders the frontmatter fields from the AI file as a structured table. */
function EntityAiFrontmatterCard(props: {
  /** When set, only these frontmatter keys are shown. */
  allowedFields?: string[];
}) {
  const { allowedFields } = props;
  const { entity } = useEntity();
  const classes = useStyles();
  const content = entity.metadata.annotations?.[AI_FILE_CONTENT_ANNOTATION];

  if (!content) return null;

  let { fields } = parseFrontmatterForDisplay(content);
  if (allowedFields) {
    fields = fields.filter(f => allowedFields.includes(f.key));
  }
  if (fields.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="File Metadata"
        subheader="Parsed from YAML frontmatter"
      />
      <Divider />
      <CardContent>
        <Table size="small" className={classes.frontmatterTable}>
          <TableBody>
            {fields.map(({ key, value }) => (
              <TableRow key={key}>
                <TableCell>{key}</TableCell>
                <TableCell>{value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

const aiOverviewContent = (
  <Grid container spacing={3} alignItems="flex-start">
    {/* Left column: About card */}
    <Grid item xs={12} md={6}>
      <CustomAboutCard
        variant="gridItem"
        hideFields={['owner', 'type', 'lifecycle', 'tags']}
      />
    </Grid>
    {/* Right column: File metadata */}
    <Grid item xs={12} md={6}>
      <EntityAiFrontmatterCard />
    </Grid>
    {/* Left column: Relations graph */}
    <Grid item xs={12} md={6}>
      <EntityCatalogGraphCard
        height={300}
        relations={[RELATION_PART_OF, RELATION_HAS_PART]}
        direction={Direction.TOP_BOTTOM}
      />
    </Grid>
    {/* Right column: File content */}
    <Grid item xs={12} md={6}>
      <EntityAiFileCard />
    </Grid>
  </Grid>
);

const skillEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {aiOverviewContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const agentEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {aiOverviewContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const ghAwEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <CustomAboutCard
            variant="gridItem"
            hideFields={['owner', 'type', 'lifecycle', 'tags']}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityAiFrontmatterCard allowedFields={['name', 'description']} />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityCatalogGraphCard
            height={300}
            relations={[RELATION_PART_OF, RELATION_HAS_PART]}
            direction={Direction.TOP_BOTTOM}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityAiFileCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const aiEntityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isAiType('skill')} children={skillEntityPage} />
    <EntitySwitch.Case if={isAiType('agent')} children={agentEntityPage} />
    <EntitySwitch.Case if={isAiType('gh-aw')} children={ghAwEntityPage} />
    {/* Fallback for any AI entity with an unrecognized type */}
    <EntitySwitch.Case>{skillEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
